import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthenticationService } from '../authentication.service';

@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss']
})
export class SignupComponent implements OnInit {
  signupForm: FormGroup;
  emailInUse: boolean = false; // ✅ inline error flag
  socialAuthError: string | null = null;
  emailLinkInfo: string | null = null;
  emailLinkError: string | null = null;

  constructor(private authService: AuthenticationService, private router: Router) {}

  ngOnInit() {
    this.signupForm = new FormGroup({
      firstname: new FormControl('', Validators.required),
      lastname: new FormControl('', Validators.required),
      country: new FormControl('', Validators.required),
      email: new FormControl('', [
        Validators.required,
        Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(com|net|org)$/)
      ]),
      password: new FormControl('', [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).+$/)
      ]),
      confirmPassword: new FormControl('', Validators.required),
      terms: new FormControl(false, Validators.requiredTrue)
    }, { validators: this.passwordsMatchValidator });

    void this.bootstrapAuthLanding();
  }

  private async bootstrapAuthLanding(): Promise<void> {
    const linkResult = await this.authService.tryCompleteEmailLinkSignInFromCurrentUrl();
    if (linkResult.status === 'signed_in' || linkResult.status === 'linked') {
      await this.router.navigateByUrl(linkResult.postNavigatePath);
      return;
    }
    if (linkResult.status === 'error') {
      this.emailLinkError = linkResult.message;
    }
    await this.finishGoogleRedirectSignIn();
  }

  // ✅ Validator for matching passwords
  passwordsMatchValidator(form: FormGroup) {
    const password = form.get('password')?.value;
    const confirmPassword = form.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { passwordsMismatch: true };
  }

  // ✅ Signup handler
  async onSignup() {
    if (!this.signupForm.valid) {
      this.signupForm.markAllAsTouched();
      return;
    }

    const data = this.signupForm.value;
    this.emailInUse = false; // reset inline error before trying again
    this.emailLinkInfo = null;
    this.emailLinkError = null;

    try {
      await this.authService.signUp(
        data.email,
        data.password,
        data.firstname,
        data.lastname,
        data.country
      );
      this.router.navigate(['/clari-sandbox']);
    } catch (err: any) {
      console.error('Signup failed:', err.message);

      if (err.code === 'auth/email-already-in-use') {
        // ✅ set inline flag
        this.emailInUse = true;
        this.signupForm.get('email')?.setErrors({ emailInUse: true });
      } else {
        alert('Signup failed: ' + err.message);
      }
    }
  }

  async onEmailLinkSignup(): Promise<void> {
    const emailControl = this.signupForm.get('email');
    emailControl?.markAsTouched();
    if (!emailControl?.valid) {
      this.emailLinkError = 'Enter a valid email to receive a sign-up link.';
      this.emailLinkInfo = null;
      return;
    }
    const terms = this.signupForm.get('terms');
    terms?.markAsTouched();
    if (!terms?.value) {
      this.emailLinkError = 'Please accept the terms to continue.';
      this.emailLinkInfo = null;
      return;
    }
    const email = String(emailControl.value || '').trim();
    this.emailLinkError = null;
    this.emailLinkInfo = null;
    try {
      await this.authService.sendEmailSignInLink(email, {
        mode: 'signin',
        continuePath: '/auth/signup',
        postAuthNavigatePath: '/clari-sandbox'
      });
      this.emailLinkInfo =
        'Check your email for the sign-in link to finish creating your account. Open it on this device when you can.';
    } catch (err: any) {
      console.error('Email link signup send failed:', err);
      this.emailLinkError = err?.message || 'Could not send sign-up link. Please try again.';
    }
  }

  async onGoogleSignup(): Promise<void> {
    this.socialAuthError = null;
    this.emailLinkInfo = null;
    this.emailLinkError = null;
    try {
      await this.authService.signInWithGoogle();
      await this.router.navigate(['/ai/clarity']);
    } catch (err: any) {
      if (err?.code === 'auth/popup-blocked') {
        this.socialAuthError = 'Popup was blocked. Continuing with Google redirect...';
        return;
      }
      console.error('Google signup failed:', err);
      this.socialAuthError = 'Google sign-up failed. Please try again.';
    }
  }

  private async finishGoogleRedirectSignIn(): Promise<void> {
    try {
      const result = await this.authService.handleGoogleRedirectResult();
      if (result) {
        await this.router.navigate(['/ai/clarity']);
      }
    } catch (err: any) {
      console.error('Google redirect signup failed:', err);
      this.socialAuthError = 'Google sign-up failed after redirect. Please try again.';
    }
  }

  // ✅ Helper for template: show mismatch warning
  get passwordsDoNotMatch(): boolean {
    const password = this.signupForm.get('password')?.value;
    const confirmPassword = this.signupForm.get('confirmPassword')?.value;
    return (
      password &&
      confirmPassword &&
      password !== confirmPassword &&
      this.signupForm.hasError('passwordsMismatch')
    );
  }

  // ✅ Helper for template: show invalid email warning
  get emailInvalid(): boolean {
    const emailControl = this.signupForm.get('email');
    return emailControl?.touched && emailControl?.invalid ? true : false;
  }
}





